import { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import api, { apiError, fmtMoney } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, ScanLine, Plus, Minus, Trash2, Loader2, Printer, Check, ShoppingCart, DoorOpen } from "lucide-react";
import { toast } from "sonner";

const PAYMENTS = ["Espèces", "Mobile Money", "Carte bancaire", "Virement"];

export default function POS() {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [payment, setPayment] = useState("Espèces");
  const [customer, setCustomer] = useState("walkin");
  const [account, setAccount] = useState("");
  const [variantModal, setVariantModal] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [opened, setOpened] = useState(true);
  const [openMap, setOpenMap] = useState({});
  const [openAmt, setOpenAmt] = useState("");
  const remindedRef = useRef(false);

  const load = () => {
    api.get(`/store/${slug}/products`).then((r) => setProducts(r.data)).catch(() => {});
    api.get(`/store/${slug}/customers`).then((r) => setCustomers(r.data)).catch(() => {});
    api.get(`/store/${slug}/treasury/opening-status`).then((r) => {
      setAccounts(r.data.accounts);
      const m = {}; r.data.accounts.forEach((a) => (m[a.id] = a.open)); setOpenMap(m);
      if (r.data.accounts[0]) setAccount((cur) => cur || r.data.accounts[0].id);
      if (r.data.accounts.some((a) => a.needs_closure) && !remindedRef.current) {
        remindedRef.current = true;
        toast.info("Des caisses ouvertes ne sont pas clôturées — pensez à la clôture Z en fin de journée.");
      }
    }).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const needOpen = account && openMap[account] === false;

  const doOpenCaisse = async () => {
    try { await api.post(`/store/${slug}/treasury/open`, { account_id: account, opening_amount: Number(openAmt || 0) }); toast.success("Caisse ouverte"); setOpenAmt(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return products;
    return products.filter((p) => p.name.toLowerCase().includes(s) || (p.sku || "").toLowerCase().includes(s) || (p.barcode || "").includes(s));
  }, [q, products]);

  const priceOf = (p) => p.sale_price || p.price;

  const pickProduct = (p) => {
    const avail = (p.variants || []).filter((v) => v.quantity > 0);
    if (avail.length === 0) { toast.error("Rupture de stock"); return; }
    if (avail.length === 1) addToCart(p, avail[0]);
    else setVariantModal(p);
  };

  const addToCart = (p, v) => {
    setCart((c) => {
      const key = `${p.id}-${v.color}-${v.size}`;
      const ex = c.find((i) => i.key === key);
      if (ex) return c.map((i) => (i.key === key ? { ...i, qty: i.qty + 1 } : i));
      return [...c, { key, product_id: p.id, name: p.name, color: v.color, size: v.size, qty: 1, price: priceOf(p) }];
    });
    setVariantModal(null);
  };

  const setQty = (key, d) => setCart((c) => c.map((i) => (i.key === key ? { ...i, qty: Math.max(1, i.qty + d) } : i)));
  const removeItem = (key) => setCart((c) => c.filter((i) => i.key !== key));

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const total = Math.max(0, subtotal - Number(discount || 0));

  const checkout = async () => {
    if (cart.length === 0) return;
    setSaving(true);
    try {
      const cust = customers.find((c) => c.id === customer);
      const { data } = await api.post(`/store/${slug}/sales`, {
        items: cart.map(({ key, ...rest }) => rest),
        discount: Number(discount || 0), payment_method: payment,
        customer_id: customer === "walkin" ? null : customer,
        customer_name: cust ? cust.name : "Client comptoir",
        account_id: account || null,
      });
      setReceipt(data);
      setCart([]); setDiscount(0);
      load();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:h-[calc(100vh-9rem)]">
      {needOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="pos-opening-gate">
          <div className="vp-card rounded-2xl p-8 max-w-sm w-full text-center">
            <div className="h-14 w-14 rounded-full grid place-items-center mx-auto mb-4" style={{ background: "rgba(61,187,44,0.15)" }}><DoorOpen className="vp-accent-text" /></div>
            <h2 className="font-display text-2xl font-bold text-white mb-1">Ouverture de caisse</h2>
            <p className="text-sm text-slate-400 mb-5">Cette caisse est fermée. Enregistrez son fond d'ouverture avant de vendre.</p>
            <Select value={account} onValueChange={setAccount}>
              <SelectTrigger className="bg-black/30 border-white/10 text-white mb-3" data-testid="pos-open-account"><SelectValue placeholder="Caisse" /></SelectTrigger>
              <SelectContent className="vp-card border-white/10 text-white">{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.open ? " ✓" : ""}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" value={openAmt} onChange={(e) => setOpenAmt(e.target.value)} placeholder="Fond d'ouverture" className="bg-black/30 border-white/10 text-white mono text-center h-12 mb-4" data-testid="pos-open-amount" />
            <Button onClick={doOpenCaisse} className="w-full h-11 vp-accent-bg text-black font-semibold" data-testid="pos-open-confirm">Ouvrir la caisse</Button>
          </div>
        </div>
      )}
      {/* Catalog */}
      <div className="lg:col-span-7 xl:col-span-8 flex flex-col min-h-0">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un produit, SKU ou code-barres…"
            className="bg-black/30 border-white/10 text-white pl-10 pr-10 h-12" data-testid="pos-search" />
          <ScanLine className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
        </div>
        <div className="overflow-y-auto no-scrollbar grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 pr-1">
          {filtered.map((p) => {
            const stock = (p.variants || []).reduce((s, v) => s + v.quantity, 0);
            return (
              <button key={p.id} onClick={() => pickProduct(p)} disabled={stock === 0}
                className="vp-card rounded-xl overflow-hidden text-left hover-lift hover:border-[var(--vp-accent)]/50 disabled:opacity-40" data-testid="pos-product-card">
                <div className="aspect-square bg-black/30 overflow-hidden">
                  {p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-slate-600"><ShoppingCart /></div>}
                </div>
                <div className="p-3">
                  <div className="text-sm font-medium text-white line-clamp-1">{p.name}</div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="mono text-sm vp-accent-text">{fmtMoney(priceOf(p))}</span>
                    <span className="text-xs text-slate-500 mono">stk {stock}</span>
                  </div>
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && <p className="text-slate-500 col-span-full text-center py-10">Aucun produit</p>}
        </div>
      </div>

      {/* Cart */}
      <div className="lg:col-span-5 xl:col-span-4 vp-card rounded-xl p-4 flex flex-col min-h-0">
        <h2 className="font-display text-xl font-bold text-white mb-3 flex items-center gap-2"><ShoppingCart size={18} /> Panier</h2>
        <div className="flex-1 overflow-y-auto no-scrollbar space-y-2 min-h-[120px]">
          {cart.length === 0 && <p className="text-sm text-slate-500 text-center py-8">Ajoutez des produits</p>}
          {cart.map((i) => (
            <div key={i.key} className="flex items-center gap-2 bg-black/20 rounded-lg p-2" data-testid="pos-cart-item">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{i.name}</div>
                <div className="text-xs text-slate-500">{i.color} · {i.size} · <span className="mono">{fmtMoney(i.price)}</span></div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setQty(i.key, -1)} className="h-6 w-6 rounded bg-white/10 grid place-items-center text-white hover:bg-white/20"><Minus size={13} /></button>
                <span className="w-6 text-center mono text-sm text-white">{i.qty}</span>
                <button onClick={() => setQty(i.key, 1)} className="h-6 w-6 rounded bg-white/10 grid place-items-center text-white hover:bg-white/20"><Plus size={13} /></button>
                <button onClick={() => removeItem(i.key)} className="h-6 w-6 rounded grid place-items-center text-slate-500 hover:text-red-400"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 mt-3 pt-3 space-y-3">
          <Select value={account} onValueChange={setAccount}>
            <SelectTrigger className="bg-black/30 border-white/10 text-white text-sm" data-testid="pos-account-select"><SelectValue placeholder="Caisse d'encaissement" /></SelectTrigger>
            <SelectContent className="vp-card border-white/10 text-white">{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{openMap[a.id] === false ? " (fermée)" : ""}</SelectItem>)}</SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2">
            <Select value={customer} onValueChange={setCustomer}>
              <SelectTrigger className="bg-black/30 border-white/10 text-white text-sm" data-testid="pos-customer-select"><SelectValue /></SelectTrigger>
              <SelectContent className="vp-card border-white/10 text-white max-h-60">
                <SelectItem value="walkin">Client comptoir</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={payment} onValueChange={setPayment}>
              <SelectTrigger className="bg-black/30 border-white/10 text-white text-sm" data-testid="pos-payment-select"><SelectValue /></SelectTrigger>
              <SelectContent className="vp-card border-white/10 text-white">{PAYMENTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between text-sm text-slate-400"><span>Sous-total</span><span className="mono text-white">{fmtMoney(subtotal)}</span></div>
          <div className="flex items-center justify-between text-sm text-slate-400">
            <span>Remise</span>
            <Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-28 h-8 bg-black/30 border-white/10 text-white text-right mono" data-testid="pos-discount" />
          </div>
          <div className="flex items-center justify-between text-lg font-bold"><span className="text-white">Total</span><span className="mono vp-accent-text" data-testid="pos-total">{fmtMoney(total)}</span></div>
          <Button onClick={checkout} disabled={cart.length === 0 || saving} className="w-full h-12 vp-accent-bg text-black font-semibold text-base" data-testid="pos-cart-checkout-btn">
            {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Check className="mr-2" size={18} />} Encaisser
          </Button>
        </div>
      </div>

      {/* Variant picker */}
      <Dialog open={!!variantModal} onOpenChange={() => setVariantModal(null)}>
        <DialogContent className="vp-card border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">{variantModal?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-2 py-2">
            {(variantModal?.variants || []).map((v, idx) => (
              <button key={idx} disabled={v.quantity === 0} onClick={() => addToCart(variantModal, v)}
                className="vp-card rounded-lg p-3 hover:border-[var(--vp-accent)] disabled:opacity-30 text-center" data-testid="pos-variant-option">
                <div className="text-sm text-white">{v.color}</div>
                <div className="text-xs text-slate-400">Taille {v.size}</div>
                <div className="text-xs mono text-slate-500 mt-1">stk {v.quantity}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt */}
      <Dialog open={!!receipt} onOpenChange={() => setReceipt(null)}>
        <DialogContent className="vp-card border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Check className="text-emerald-400" /> Vente enregistrée</DialogTitle></DialogHeader>
          {receipt && (
            <div className="space-y-2 py-2">
              <div className="text-center border-b border-white/10 pb-3">
                <div className="font-display text-xl font-bold">Vision POS</div>
                <div className="mono text-sm text-slate-400">{receipt.number}</div>
              </div>
              {receipt.items.map((i, idx) => (
                <div key={idx} className="flex justify-between text-sm"><span className="text-slate-300">{i.qty}× {i.name} <span className="text-slate-500">({i.color}/{i.size})</span></span><span className="mono">{fmtMoney(i.price * i.qty)}</span></div>
              ))}
              <div className="border-t border-white/10 pt-2 flex justify-between"><span>Remise</span><span className="mono">{fmtMoney(receipt.discount)}</span></div>
              <div className="flex justify-between text-lg font-bold"><span>Total</span><span className="mono vp-accent-text">{fmtMoney(receipt.total)}</span></div>
              <div className="text-xs text-slate-500 text-center pt-2">{receipt.payment_method} · {receipt.customer_name}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => window.print()} className="text-slate-300"><Printer size={15} className="mr-1" /> Imprimer</Button>
            <Button onClick={() => setReceipt(null)} className="vp-accent-bg text-black font-medium" data-testid="pos-new-sale-btn">Nouvelle vente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
